import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import logo from '../../../assets/logos/new_logo_black_3.png'
import { QRCodeSVG } from 'qrcode.react'
import './DownloadApps.css'

// Ad-hoc (non-Play) distribution page for the WiL Reji POS mobile apps.
// Sideloaded Android users have no Play/OTA update discovery, so this page is
// their download + update surface. Version/changelog/APK URL are read from the
// SAME runtime config doc the apps consume (config.wilreji.com/mobile/<env>.json)
// so the page can never drift from the shipped build — see
// local-docs/SPEC-app-update-distribution.md §4.
//
// Fail-closed: config is fetched at render; if it is missing, malformed, or the
// fetch fails, the page STILL renders (hero, install steps, QR, support) and
// each app card degrades to a "contact support" state — never a blank screen,
// never a dead download button. This is the DEFAULT state until the P0 infra
// (schemaVersion 2 config + CORS) lands, so it is the primary path.
//
// i18n: strings live in src/Locales/{en,ja}.json under the web_download_*
// keys, same convention as every other website/* page (see Page404.js,
// shared/header.js). The site header language dropdown is the single
// language control — this page has none of its own.

const SUPPORT_EMAIL = 'contact@willdesign-tech.com'
// TODO(owner): supply the real support phone number. No phone exists anywhere
// in the site today — email is the only verified channel — so the phone row
// renders a placeholder rather than a fabricated (dialable) number.
const SUPPORT_PHONE = null

// The page derives its env from the host it is served on, then reads the
// matching mobile runtime config. This keeps a staging/preprod page pointed at
// the staging/preprod build channels and a prod page at the prod store links —
// no build-time flag needed. localhost is treated as staging (dev).
export const resolveEnv = (hostname) => {
  // Match `staging`/`preprod` as a host LABEL (start or after a dot) so both the
  // bare env host (staging.wilreji.com) and app subdomains ({app}.staging.wilreji.com,
  // the org domain convention) resolve correctly.
  if (/(^|\.)staging\./i.test(hostname) || hostname === 'localhost' || hostname === '127.0.0.1') return 'staging'
  if (/(^|\.)preprod\./i.test(hostname)) return 'preprod'
  return 'prod'
}
const mobileConfigUrl = (env) => `https://config.wilreji.com/mobile/${env}.json`

// iOS apps ship via the App Store (region-neutral links resolve to the
// viewer's local store). Static ids — not in the runtime config, which only
// carries the Android sideload block.
const IOS_APP_STORE = {
  waiter: 'https://apps.apple.com/app/id6748054190',
  cashier: 'https://apps.apple.com/app/id6746717803',
}

const APP_KEYS = ['waiter', 'cashier']
const STEP_NUMBERS = [1, 2, 3, 4, 5]
// Screenshots stay JA on purpose (Inap supplies JA device UI); only step 5
// has no screenshot yet. Captions/alt text still follow the selected language.
const STEP_IMAGES = { 1: 'download-notification', 2: 'chrome-apk-warning', 3: 'unknown-sources', 4: 'play-protect', 5: null }

const getPlatform = () => {
  const ua = (navigator.userAgent || '').toLowerCase()
  if (/android/.test(ua)) return 'android'
  if (/iphone|ipad|ipod/.test(ua)) return 'ios'
  return 'desktop'
}

// Install-channel URLs come from the runtime config document, which is unsigned —
// whoever can write it chooses what these buttons point at. https alone is not a
// check, so the host is pinned too.
//
// Two tiers, because the risks differ: a bad STORE link at worst sends someone to
// the wrong listing, which the store itself then identifies. A bad BINARY link
// hands them an APK to install on a device that runs the till.
const STORE_HOSTS = [/^apps\.apple\.com$/i, /^testflight\.apple\.com$/i, /^play\.google\.com$/i]

// Binaries may only come from a host WE publish to, so a config compromise costs
// a missing button rather than a hostile install. Deliberately excluded:
//   expo.dev  — any Expo account can mint https://expo.dev/artifacts/eas/<id>.apk,
//               so the host proves nothing about who built it
//   drive.google.com / drive.usercontent.google.com — open upload, same problem
//   *.wilreji.com as a wildcard — a forgotten or taken-over subdomain, or any
//               subdomain serving user content, would qualify
const BINARY_HOSTS = [/^downloads(\.(staging|preprod))?\.wilreji\.com$/i]

// Returns the NORMALISED url (parsed.href), not the raw input, so what was
// validated is exactly what gets rendered and encoded into the QR code.
const urlOnHosts = (u, hosts) => {
  if (typeof u !== 'string') return null
  let parsed
  try {
    parsed = new URL(u)
  } catch {
    return null // not an absolute URL at all
  }
  if (parsed.protocol !== 'https:') return null
  // Reject embedded credentials and non-default ports. Neither is legitimate for
  // a published install link, and the host check alone would pass both:
  // `https://evil:pw@downloads.wilreji.com/x.apk` has the right hostname while
  // leaking credentials into the rendered link and the QR code.
  if (parsed.username || parsed.password || parsed.port) return null
  return hosts.some((host) => host.test(parsed.hostname)) ? parsed.href : null
}

const storeUrl = (u) => urlOnHosts(u, STORE_HOSTS)
const binaryUrl = (u) => urlOnHosts(u, BINARY_HOSTS)

// Build one app's install-channel model from the runtime config + env policy.
// This page is a PERMANENT, data-driven install hub — each platform renders one
// button+QR+copy per available channel. A channel is shown only when BOTH the
// config supplies its (https) url AND the env policy below allows it:
//
//   env          iOS channels                     Android channels
//   prod         App Store                        Play Store, APK (interim)   ← STORE ONLY, no beta
//   staging/     App Store (opt.), TestFlight/     APK, Play Store (opt.)      ← testing/QA set
//   preprod      ad-hoc test build
//
// The beta/test install (TestFlight or ad-hoc EAS build) is NEVER shown on prod,
// even if the config carries a url — the owner keeps beta channels off the public
// page. Every other channel is gated purely on config-url presence, so a new
// route appears by adding a config field. A channel whose url is absent/non-https
// is silently omitted (never a dead/unsafe button); a platform is NEVER hidden.
// changelog reads android.changelog, falling back to top-level apps.<app>.changelog.
export const readApp = (config, key, t, lang, env) => {
  const appCfg = (config && config.apps && config.apps[key]) || {}
  const iosCfg = appCfg.ios || {}
  const andCfg = appCfg.android || {}

  // iOS: App Store always present via the static id fallback (all envs).
  const iosChannels = []
  const appStoreUrl = storeUrl(iosCfg.appStoreUrl) || IOS_APP_STORE[key]
  if (appStoreUrl) iosChannels.push({ id: 'appstore', label: t('web_download_ios_open_store'), url: appStoreUrl })
  // Beta/test install — testing envs only, never prod.
  const testUrl = storeUrl(iosCfg.testFlightUrl || iosCfg.testUrl)
  if (testUrl && env !== 'prod') iosChannels.push({ id: 'testflight', label: t('web_download_ios_testflight'), url: testUrl })

  // Android: Play Store + direct APK. Both allowed on every env (on testing envs
  // the APK is the latest env build; on prod it is the interim store substitute).
  const androidChannels = []
  const playStoreUrl = storeUrl(andCfg.playStoreUrl)
  if (playStoreUrl) androidChannels.push({ id: 'play', label: t('web_download_android_play'), url: playStoreUrl })
  const apkUrl = binaryUrl(andCfg.apkUrl)
  if (apkUrl) androidChannels.push({ id: 'apk', label: t('web_download_btn'), url: apkUrl, download: true })

  const rawChangelog = (andCfg.changelog || appCfg.changelog || {})[lang]
  return {
    iosChannels,
    androidChannels,
    version: andCfg.latestVersion || null,
    build: andCfg.latestBuild || null,
    releasedAt: andCfg.releasedAt || null, // optional; schema has no date field yet
    changelog: typeof rawChangelog === 'string' ? rawChangelog : null,
  }
}

const BTN_MIN = { minHeight: '48px' }
const QR_SIZE = 132

// Copy-to-clipboard button with a transient "Copied!" confirmation. Fails
// closed: if the Clipboard API is missing (insecure context / old browser) or
// rejects (permission denied), it drops to a read-only, pre-selected input so
// the URL stays copyable by hand — the link is never lost, per the spec.
const CopyLinkButton = ({ url, t }) => {
  const [state, setState] = useState('idle') // idle | copied | manual
  const resetTimer = useRef()
  // Clear the pending "Copied!"→idle reset on unmount so we never setState after
  // the component is gone (user navigates away within the 2s window).
  useEffect(() => () => clearTimeout(resetTimer.current), [])
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
        setState('copied')
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(() => setState('idle'), 2000)
        return
      }
    } catch {
      // fall through to the manual selectable-link fallback
    }
    setState('manual')
  }
  return (
    <div className='dl-copy'>
      <button type='button' className='btn btn-sm dl-toggle w-100' style={BTN_MIN} onClick={copy}>
        <span role='status'>{state === 'copied' ? t('web_download_copied') : t('web_download_copy_link')}</span>
      </button>
      {state === 'manual' && (
        <label className='dl-copy-manual d-block mt-2'>
          <span className='visually-hidden'>{t('web_download_copy_fallback')}</span>
          {/* autoFocus + select(): the input appears only on an explicit Copy click,
              so focusing it and selecting the url in one step is the expected UX. */}
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <input
            className='form-control form-control-sm dl-copy-url'
            readOnly
            autoFocus
            value={url}
            onFocus={(e) => e.currentTarget.select()}
          />
        </label>
      )}
    </div>
  )
}

// One install channel: a scannable QR + primary button + copy-link, all pointing
// at the same url. External stores open in a new tab; the APK uses `download`.
const ChannelBlock = ({ channel, appName, t }) => (
  <div className='dl-channel'>
    <div className='text-center'>
      <QRCodeSVG
        value={channel.url}
        size={QR_SIZE}
        className='dl-block-qr mb-3'
        title={`${appName} — ${channel.label}`}
      />
    </div>
    <a
      className='btn dl-cta w-100 mb-2'
      style={BTN_MIN}
      href={channel.url}
      {...(channel.download ? { download: true } : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {channel.label}
    </a>
    <CopyLinkButton url={channel.url} t={t} />
  </div>
)

// iOS platform card — renders every available iOS channel (App Store always,
// TestFlight when config supplies a join url). Never fail-closed: the static
// App Store id guarantees at least one channel.
const IosBlock = ({ appKey, t, channels, primary }) => (
  <div className={`dl-platform${primary ? ' dl-platform-primary' : ''}`}>
    <div className='dl-platform-head'>
      <span className='badge bg-dark'>{t('web_download_ios_chip')}</span>
    </div>
    {channels.map((c) => (
      <ChannelBlock key={c.id} channel={c} appName={t(`web_download_app_${appKey}_name`)} t={t} />
    ))}
  </div>
)

// Android platform card — renders every available Android channel (Play Store
// when config supplies a url, direct APK when a valid https apkUrl exists).
// Fail-closed is DECOUPLED from platform visibility: the contact-support state
// shows ONLY when the config yields no Android channel — never because a platform
// is hidden. While the config is still loading, show a spinner, not fail-closed.
const AndroidBlock = ({ appKey, t, loading, data, primary }) => {
  const channels = data.androidChannels
  return (
    <div className={`dl-platform${primary ? ' dl-platform-primary' : ''}`}>
      <div className='dl-platform-head d-flex flex-wrap align-items-center gap-2'>
        <span className='badge bg-success'>{t('web_download_android_chip')}</span>
        {data.version && (
          <span className='text-muted' style={{ fontSize: '0.85rem' }}>
            {t('web_download_version_label')} {data.version}
            {data.build ? ` (${t('web_download_build_label')} ${data.build})` : ''}
            {data.releasedAt ? ` · ${data.releasedAt}` : ''}
          </span>
        )}
      </div>
      {data.changelog && (
        <p className='card-text small mb-2'>
          <span className='fw-semibold'>{t('web_download_changelog_label')}: </span>
          {data.changelog}
        </p>
      )}
      {loading ? (
        <div className='text-muted' role='status'>
          {t('web_download_card_loading')}
        </div>
      ) : channels.length ? (
        channels.map((c) => (
          <ChannelBlock key={c.id} channel={c} appName={t(`web_download_app_${appKey}_name`)} t={t} />
        ))
      ) : (
        <div className='alert alert-warning mb-0' role='status'>
          {t('web_download_card_unavailable')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
      )}
    </div>
  )
}

// Every visitor sees BOTH platforms for every app — a download page must not
// hide options by UA. The detected platform is only used to order/emphasize:
// the visitor's own platform renders first and gets the `primary` highlight.
const AppCard = ({ appKey, t, platform, loading, data }) => {
  const iosFirst = platform === 'ios'
  const ios = <IosBlock key='ios' appKey={appKey} t={t} channels={data.iosChannels} primary={iosFirst} />
  const android = <AndroidBlock key='android' appKey={appKey} t={t} loading={loading} data={data} primary={!iosFirst} />
  return (
    <div className='card dl-card dl-app-card h-100 shadow-sm'>
      <div className='card-body d-flex flex-column p-3 p-md-4'>
        <div className='mb-3'>
          <h2 className='h4 mb-1'>{t(`web_download_app_${appKey}_name`)}</h2>
          <div className='text-muted' style={{ fontSize: '0.95rem' }}>
            {t(`web_download_app_${appKey}_device`)}
          </div>
        </div>
        <div className='dl-platforms'>{iosFirst ? [ios, android] : [android, ios]}</div>
      </div>
    </div>
  )
}

const ScreenshotSlot = ({ img, alt }) => {
  if (!img) return null
  // TODO(Inap): replace with the annotated JA screenshot named `${img}` once
  // supplied. The assets are not shipped yet, so onError hides the element
  // rather than leaving a broken-image icon in the redesigned layout. The
  // src/alt stay unchanged (a11y + tests) — only display is toggled off.
  return (
    <img
      src={`/assets/download/${img}.png`}
      alt={alt}
      className='img-fluid rounded border mt-2 dl-shot'
      style={{ maxWidth: '320px', background: '#f1f3f5' }}
      loading='lazy'
      onError={(e) => {
        e.currentTarget.style.display = 'none'
      }}
    />
  )
}

const DownloadApps = () => {
  const { t, i18n } = useTranslation()
  // Header dropdown only offers en/ja (see shared/header.js); normalize any
  // other detected locale (e.g. browser "en-US") to the pair the runtime
  // config's changelog object is keyed by.
  // startsWith, not ===: i18next is not configured with load:'languageOnly',
  // so a region-coded value ('ja-JP') would fall through to the English
  // changelog on a Japanese page.
  const lang = i18n.language?.startsWith('ja') ? 'ja' : 'en'

  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [platform] = useState(getPlatform)
  const [env] = useState(() => resolveEnv(window.location.hostname))
  const [stepsOpen, setStepsOpen] = useState(() => getPlatform() === 'android')

  // Page-URL QR points at THIS host's /download (so a staging visitor's QR opens
  // the staging page, not prod).
  const downloadPageUrl = `${window.location.origin}/download`

  useEffect(() => {
    let alive = true
    // Cache-bust so the page always reflects the freshly published config.
    const fetchJson = (url) =>
      fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' }).then((r) => {
        if (!r.ok) throw new Error(`config ${r.status}`)
        return r.json()
      })
    fetchJson(mobileConfigUrl(env))
      // preprod config may not be published yet → fall back to the staging build.
      .catch(() => (env === 'preprod' ? fetchJson(mobileConfigUrl('staging')) : Promise.reject(new Error('no config'))))
      .then((json) => {
        if (alive) setConfig(json)
      })
      .catch(() => {
        // Fail-closed: leave config null; cards degrade to contact-support.
        if (alive) setConfig(null)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [env])

  // Desktop only: iOS cards already show their own App Store QR, Android UA
  // gets the direct APK button — the page-URL QR is for a desktop visitor to
  // hop to their phone.
  const showQr = platform === 'desktop'

  const cards = APP_KEYS.map((key) => (
    // col-12 on phones (stacked, no 360px overflow); two-up from ≥sm.
    <div className='col-12 col-sm-6 mb-4' key={key}>
      <AppCard appKey={key} t={t} platform={platform} loading={loading} data={readApp(config, key, t, lang, env)} />
    </div>
  ))

  return (
    <div className='download-page privacy-policy-pages py-4 py-md-5' style={{ fontSize: '16px' }}>
      <div className='container'>
        {/* Hero band — wraps cleanly at 360px, roomy from ≥md. */}
        <div className='dl-hero p-3 p-md-4 mb-4'>
          <div className='d-flex flex-column flex-md-row align-items-md-center gap-3'>
            <img src={logo} alt='WiL Reji' className='dl-hero-logo' />
            <div>
              <h1 className='h3 mb-1'>{t('web_download_hero_title')}</h1>
              <p className='text-muted mb-0'>{t('web_download_hero_subtitle')}</p>
            </div>
          </div>
          <p className='mb-0 mt-3'>{t('web_download_reassurance')}</p>
        </div>

        <div className='row'>
          {/* Main column */}
          <div className='col-lg-8'>
            <div className='row'>{cards}</div>

            {/* Numbered install steps (collapsible; expanded on Android) */}
            <div className='card dl-card mb-4'>
              <div className='card-body p-3 p-md-4'>
                <div className='d-flex flex-wrap justify-content-between align-items-center gap-2'>
                  <h2 className='h4 mb-0'>{t('web_download_steps_title')}</h2>
                  <button
                    type='button'
                    className='btn btn-sm dl-toggle'
                    style={BTN_MIN}
                    aria-expanded={stepsOpen}
                    onClick={() => setStepsOpen((v) => !v)}
                  >
                    {stepsOpen ? t('web_download_steps_toggle_close') : t('web_download_steps_toggle_open')}
                  </button>
                </div>
                {stepsOpen && (
                  <ol className='list-unstyled mt-3 mb-0'>
                    {STEP_NUMBERS.map((n) => {
                      const img = STEP_IMAGES[n]
                      return (
                        <li className='dl-step mb-4' key={n}>
                          <span className='dl-step-num' aria-hidden='true'>
                            {n}
                          </span>
                          <div>
                            <h3 className='h6'>{t(`web_download_step${n}_title`)}</h3>
                            <p className='mb-1'>{t(`web_download_step${n}_body`)}</p>
                            <ScreenshotSlot img={img} alt={img ? t(`web_download_step${n}_alt`) : null} />
                          </div>
                        </li>
                      )
                    })}
                  </ol>
                )}
              </div>
            </div>

            {/* Troubleshooting — Android APK-signature error. Every visitor now
                sees the Android sideload block, so this is relevant to all. */}
            <div className='card dl-card mb-4'>
              <div className='card-body p-3 p-md-4'>
                <h2 className='h5'>{t('web_download_troubleshoot_title')}</h2>
                <p className='mb-0'>
                  {t('web_download_troubleshoot_text')}{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              </div>
            </div>

            {/* Support footer */}
            <div className='card dl-card'>
              <div className='card-body p-3 p-md-4'>
                <h2 className='h5'>{t('web_download_support_title')}</h2>
                <p className='mb-1'>{t('web_download_support_note')}</p>
                {SUPPORT_PHONE && (
                  <p className='mb-1'>
                    {t('web_download_phone_label')}: <a href={`tel:${SUPPORT_PHONE.replace(/-/g, '')}`}>{SUPPORT_PHONE}</a>
                  </p>
                )}
                <p className='mb-0'>
                  {t('web_download_email_label')}: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              </div>
            </div>
          </div>

          {/* QR sidebar — desktop only (≥lg), hidden on Android/iOS UA */}
          {showQr && (
            <div className='col-lg-4 d-none d-lg-block'>
              <div className='card dl-card dl-sidebar-sticky'>
                <div className='card-body text-center p-4'>
                  <h2 className='h5'>{t('web_download_qr_title')}</h2>
                  {/* Live SVG QR of the (stable) download-page URL — rendered
                      client-side, so there is no PNG asset to ship or 404. */}
                  <QRCodeSVG
                    value={downloadPageUrl}
                    size={220}
                    className='dl-qr'
                    title={t('web_download_qr_alt', { url: downloadPageUrl })}
                  />
                  <p className='text-muted mt-3 mb-0' style={{ fontSize: '0.9rem' }}>
                    {t('web_download_qr_note')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DownloadApps
